import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import useSiteConfig from './hooks/useSiteConfig';
import { metaFor } from './pageMeta';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './components/Login';
import Register from './components/Register';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import KYC from './components/KYC';
import Payments from './components/Payments';
import AuditLogs from './components/AuditLogs';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Terms from './components/Terms';
import Privacy from './components/Privacy';
import FAQ from './components/FAQ';
import InvestorGuide from './components/InvestorGuide';
import Footer from './components/Footer';
import RoleRoute from './components/RoleRoute';
import LeadsPipeline from './components/LeadsPipeline';
import KycAadhaarCallback from './components/KycAadhaarCallback';
import KycReview from './components/KycReview';
import Deals from './components/Deals';
import DealDetail from './components/DealDetail';
import AdminSpvs from './components/AdminSpvs';
import AdminSpvEdit from './components/AdminSpvEdit';
import MyCommitments from './components/MyCommitments';
import AdminPayments from './components/AdminPayments';
import MyDocuments from './components/MyDocuments';
import EsignCallback from './components/EsignCallback';
import AdminAllotment from './components/AdminAllotment';
import AdminDistribution from './components/AdminDistribution';
import ExpenseReport from './components/ExpenseReport';
import Resale from './components/Resale';
import AdminResale from './components/AdminResale';
import Contact from './components/Contact';
import About from './components/About';
import McaCompliance from './components/McaCompliance';
import NotFound from './components/NotFound';
import RiskDisclosure from './components/RiskDisclosure';

const AppRoutes = () => {
  const location = useLocation();
  const { brand_name: brand } = useSiteConfig();

  useEffect(() => {
    const { title, description } = metaFor(location.pathname, brand);
    document.title = title;
    const tag = document.querySelector('meta[name="description"]');
    if (tag && description) tag.setAttribute('content', description);
  }, [location.pathname, brand]);

  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, [location]);

  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/kyc" element={
          <ProtectedRoute>
            <KYC />
          </ProtectedRoute>
        } />
        <Route path="/payments" element={
          <ProtectedRoute>
            <Payments />
          </ProtectedRoute>
        } />
        <Route path="/audit" element={
          <ProtectedRoute>
            <AuditLogs />
          </ProtectedRoute>
        } />
        <Route path="/kyc/aadhaar/callback" element={
          <ProtectedRoute>
            <KycAadhaarCallback />
          </ProtectedRoute>
        } />
        <Route path="/admin/kyc" element={
          <RoleRoute roles={['admin', 'superadmin', 'compliance']}>
            <KycReview />
          </RoleRoute>
        } />
        {/* Retired pre-SPV pages */}
        <Route path="/properties" element={<Navigate to="/deals" replace />} />
        <Route path="/properties/:id" element={<Navigate to="/deals" replace />} />
        <Route path="/marketplace" element={<Navigate to="/deals" replace />} />
        <Route path="/fractions" element={<Navigate to="/dashboard" replace />} />
        <Route path="/deals" element={<ProtectedRoute><Deals /></ProtectedRoute>} />
        <Route path="/deals/:id" element={<ProtectedRoute><DealDetail /></ProtectedRoute>} />
        <Route path="/admin/spvs" element={<RoleRoute roles={['admin', 'superadmin']}><AdminSpvs /></RoleRoute>} />
        <Route path="/admin/spvs/:id" element={<RoleRoute roles={['admin', 'superadmin']}><AdminSpvEdit /></RoleRoute>} />
        <Route path="/commitments" element={<ProtectedRoute><MyCommitments /></ProtectedRoute>} />
        <Route path="/admin/payments" element={<RoleRoute roles={['admin', 'superadmin']}><AdminPayments /></RoleRoute>} />
        <Route path="/my-documents" element={<ProtectedRoute><MyDocuments /></ProtectedRoute>} />
        <Route path="/esign/callback" element={<ProtectedRoute><EsignCallback /></ProtectedRoute>} />
        <Route path="/admin/spvs/:id/allotment" element={<RoleRoute roles={['admin', 'superadmin']}><AdminAllotment /></RoleRoute>} />
        <Route path="/admin/spvs/:id/distribution" element={<RoleRoute roles={['admin', 'superadmin']}><AdminDistribution /></RoleRoute>} />
        <Route path="/spvs/:id/expenses" element={<ProtectedRoute><ExpenseReport /></ProtectedRoute>} />
        <Route path="/resale" element={<ProtectedRoute><Resale /></ProtectedRoute>} />
        <Route path="/admin/resale" element={<RoleRoute roles={['admin', 'superadmin']}><AdminResale /></RoleRoute>} />
        <Route path="/rm/leads" element={
          <RoleRoute roles={['rm', 'admin', 'superadmin']}>
            <LeadsPipeline />
          </RoleRoute>
        } />
        <Route path="/investment-guide" element={<InvestorGuide />} />
        <Route path="/risk-disclosure" element={<RiskDisclosure />} />
        <Route path="/mca-compliant" element={<McaCompliance />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />


        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <ToastContainer />
    </>
  );
};

const App = () => (
  <AuthProvider>
    <Router>
      <AppRoutes />
      <ToastContainer />
    </Router>
  </AuthProvider>
);

export default App;
